import { Head } from "@inertiajs/react"
import {
  BarChart3,
  ListChecks,
  PauseCircle,
  PlayCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react"
import { LandingFooter } from "@/components/landing/footer"
import { LandingHeader } from "@/components/landing/header"
import { GoogleButton } from "@/components/landing/google-button"

interface HomeProps {
  signedIn: boolean
}

const tools = [
  {
    icon: ListChecks,
    title: "List campaigns & accounts",
    body: "See every Google Ads account you can access and inspect every campaign — status, budget, and whether ads are actually serving.",
  },
  {
    icon: BarChart3,
    title: "Performance metrics",
    body: "Pull impressions, clicks, CTR, CPC, cost, conversions, and conversion rate over any range from the last 7 to the last 30 days.",
  },
  {
    icon: Search,
    title: "Search terms reports",
    body: "Surface the actual queries that triggered your ads and find negative-keyword candidates ranked by wasted spend.",
  },
  {
    icon: ShieldCheck,
    title: "Negative keywords",
    body: "Add tight, focused negative-keyword lists from inside Claude. Preview before applying — no surprises.",
  },
  {
    icon: PlayCircle,
    title: "Pause & enable",
    body: "Flip campaigns on or off without touching the Ads UI. Same goes for budget updates and ad-copy edits.",
  },
  {
    icon: Wallet,
    title: "Budget control",
    body: "Adjust daily budgets safely with confirmation. Built so an agent can take action, not just report on it.",
  },
]

export default function Home({ signedIn }: HomeProps) {
  return (
    <>
      <Head title="AdWizard — Run Google Ads from Claude" />
      <div className="flex min-h-screen flex-col bg-background">
        <LandingHeader signedIn={signedIn} />

        <main className="flex-1">
          <section className="mx-auto max-w-6xl px-6 pt-20 pb-24 sm:pt-28">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Model Context Protocol server for Google Ads
              </div>
              <h1 className="text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
                Run your Google Ads campaigns from Claude.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground sm:text-xl">
                Connect your Google Ads account once and give Claude direct
                access to your campaigns. Pull search terms, add negative
                keywords, adjust budgets, and check performance — all from a
                conversation.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                {signedIn ? (
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-medium text-primary-foreground hover:opacity-90"
                  >
                    Go to dashboard
                  </a>
                ) : (
                  <GoogleButton label="Sign in with Google" />
                )}
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 font-medium text-foreground hover:bg-card"
                >
                  See how it works
                </a>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Sign-in requests read-only and write access to your Google Ads
                accounts. You can revoke access from your Google account at any
                time.
              </p>
            </div>
          </section>

          <section
            id="how-it-works"
            className="border-t border-border bg-card/40"
          >
            <div className="mx-auto max-w-6xl px-6 py-20">
              <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
                Three steps to ship.
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                The whole setup takes about two minutes. After that, every new
                Claude conversation has your Google Ads tools available.
              </p>

              <ol className="mt-12 grid gap-6 sm:grid-cols-3">
                <li className="rounded-lg border border-border bg-background p-6">
                  <div className="text-sm font-mono text-primary">01</div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">
                    Sign in with Google
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Authenticate with the Google account that has access to
                    your Google Ads. We never see or store your password.
                  </p>
                </li>
                <li className="rounded-lg border border-border bg-background p-6">
                  <div className="text-sm font-mono text-primary">02</div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">
                    Add the MCP to Claude
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Run one command in Claude Code to register this server.
                    Claude handles the OAuth handshake from there.
                  </p>
                  <pre className="mt-4 overflow-x-auto rounded-md bg-secondary p-3 text-xs text-secondary-foreground">
                    <code>
                      claude mcp add --transport http \{"\n"}
                      {"  "}adwizard https://tryadwizard.com/mcp
                    </code>
                  </pre>
                </li>
                <li className="rounded-lg border border-border bg-background p-6">
                  <div className="text-sm font-mono text-primary">03</div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">
                    Just ask Claude
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    "Check my ads," "pull search terms for the X campaign,"
                    "add these negatives." Claude calls the tools and reports
                    back.
                  </p>
                </li>
              </ol>
            </div>
          </section>

          <section id="tools" className="mx-auto max-w-6xl px-6 py-20">
            <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
              What Claude can do for you.
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Twelve tools, every one designed so an agent can take action —
              not just describe it.
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {tools.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-lg border border-border bg-card p-6"
                >
                  <Icon className="h-6 w-6 text-primary" />
                  <h3 className="mt-4 text-lg font-semibold text-foreground">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{body}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="border-t border-border">
            <div className="mx-auto max-w-3xl px-6 py-20 text-center">
              <PauseCircle className="mx-auto h-10 w-10 text-primary" />
              <h2 className="mt-6 text-3xl font-semibold text-foreground sm:text-4xl">
                Stop tab-hopping. Start asking.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Connect your Google Ads to Claude and run optimization
                conversations the way you'd talk to an analyst.
              </p>
              <div className="mt-8 flex justify-center">
                {signedIn ? (
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-medium text-primary-foreground hover:opacity-90"
                  >
                    Go to dashboard
                  </a>
                ) : (
                  <GoogleButton label="Get started — Sign in with Google" />
                )}
              </div>
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
